// Poll domain constants, role helpers and the client-side surface-state resolver.
//
// The admin poll endpoints (/admin/polls/*) return the raw Poll document and do NOT
// include the resolved `state` — only the fan endpoint (/polls/list) computes it. The
// CMS therefore mirrors the backend's `lib/pollState.ts#resolvePollSurfaceState` here so
// the list/table can show an accurate read-only state badge. The admin list/get
// endpoints populate `game` (with `date` + `gameStatus`), which is all the resolver needs.

export const POLL_TYPE = {
  // Prediction tied to a specific match; lifecycle driven by match status.
  GAME_DAY: 'game_day',
  // Opinion/engagement poll scheduled by a date window.
  NON_GAME_DAY: 'non_game_day'
};

export const POLL_SOURCE = {
  ADMIN: 'admin',
  // Auto-generated from the match lineup (e.g. "who scores first").
  AUTO: 'auto'
};

export const POLL_STATE = {
  OPEN: 'open',
  CLOSED: 'closed',
  HIDDEN: 'hidden'
};

export const POLL_ROLE = {
  P4P: 'p4p',
  TEAM: 'team'
};

// 2–6 admin-authored options (auto/lineup polls can exceed this, but those aren't
// authored here). Default reward mirrors the backend's DEFAULT_REWARD_POINTS.
// 2–6 options for all admin-authored polls. For game-day polls the admin picks 2–6 players
// from the squad/lineup; auto-generated lineup polls (not authored here) can exceed this.
export const POLL_MIN_OPTIONS = 2;
export const POLL_MAX_OPTIONS = 6;
export const POLL_DEFAULT_REWARD_POINTS = 5;

export const getPollRole = (user) => {
  if (!user) return null;
  if (user.admin) return POLL_ROLE.P4P;
  if (user.type === 'team') return POLL_ROLE.TEAM;
  return null;
};

// P4P admins manage any channel; a team (channel owner) admin manages only their own
// channel (their own user id IS the team/channel id). Mirrors backend requirePollAdmin.
export const canManagePollChannel = (user, teamId) => {
  const role = getPollRole(user);
  if (role === POLL_ROLE.P4P) return true;
  if (role === POLL_ROLE.TEAM) return user?._id === teamId;
  return false;
};

// The phase-to-state derivation that used to live here has been DELETED.
//
// It was a JavaScript reimplementation of the backend resolvers, written because the admin
// endpoints returned the raw document with no resolved "state". It had already drifted: it closed
// its kickoff-offset half-time window at +46 min where the backend used +65, knew nothing about
// postponed/cancelled fixtures, and its own comment conceded it could not see the Daily Quick Quiz
// replacement at all — so an admin configured one rotation and was shown another.
//
// /admin/polls/list, /admin/polls/get and /admin/polls/update now all return "state", resolved by
// the same code the fan-facing surfaces use. Read poll.state; never re-derive it. The same applies
// to quizzes: /admin/quizzes/list returns "state" too.


// Formats a populated upcomingGames entry for the game picker / table.
export const getPollGameLabel = (game) => {
  if (!game) return '';
  const home = game.homeName || game.home?.name || 'TBD';
  const away = game.awayName || game.away?.name || 'TBD';
  return `${home} vs ${away}`;
};
